import { DgcActivationLink, DgcOrderCard, DgcOrderDetail } from 'api/types/dgc/dgcOrderDetail';
import { DateType } from 'shared/types/dates';

// Zero-padded, unlike DigitalGiftCards' formatDate — OMS validates created_from/created_to against a strict yyyy-mm-dd pattern.
export const formatDate = (date: DateType) => {
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = date.getFullYear();
  return `${yy}-${mm}-${dd}`;
};

// OMS returns subtotal as a number but the fee/total fields as numeric strings (Postgres `numeric` columns); both need the same display format.
export const formatAmount = (value: string | number | null | undefined, currency: string): string => {
  if (value === null || value === undefined) return '—';
  return `${Number(value).toFixed(2)} ${currency}`;
};

export const formatPoints = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString('en', { maximumFractionDigits: 2 });
};

// OMS stores the points' monetary value in minor units (e.g. 19240 fils = 192.40 AED).
export const formatMinorAmount = (value: string | number | null | undefined, currency: string): string => {
  if (value === null || value === undefined) return '—';
  return formatAmount(Number(value) / 100, currency);
};

// The rate is how many points are worth one unit of the order currency.
export const formatPointsRate = (rate: string | number | null | undefined, currency: string): string => {
  if (rate === null || rate === undefined) return '—';
  return `${formatPoints(rate)} points = 1 ${currency}`;
};

const CANCEL_ERROR_FALLBACK = 'Could not cancel this order. Please try again.';

// The BFF passes an OMS business-rule message through as response.data.message; anything else falls back.
export const getApiErrorMessage = (error: unknown, fallback: string = CANCEL_ERROR_FALLBACK): string => {
  const message = (error as any)?.response?.data?.message;
  return typeof message === 'string' && message.length > 0 ? message : fallback;
};

// Mirrors OMS's resend eligibility checks in AdminCardActivationLinkService.resendSms.
export function getResendBlockedReason({
  order,
  card,
  link,
}: {
  order: DgcOrderDetail;
  card: DgcOrderCard;
  link: DgcActivationLink;
}): string | null {
  if (order.type !== 'B2C') {
    return 'Only B2C orders can be resent.';
  }
  if (order.status === 'CANCELLED') {
    return 'This order has been cancelled.';
  }
  if (link.status === 'completed' || link.completed_at) {
    return 'This activation is already completed.';
  }
  if (link.status === 'expired') {
    return 'This activation link has expired.';
  }
  if (link.linked_at) {
    return 'This card has already been linked to a user.';
  }
  if (!card.recipient_mobile) {
    return 'This recipient has no mobile number on file.';
  }
  return null;
}

// A PAID physical order on Premo is normally in flight for seconds (OMS ADR 0004); beyond this it is likely stuck.
export const STUCK_PAID_THRESHOLD_MS = 15 * 60 * 1000;

// Explains the order status for physical orders using the issuance semantics in OMS ADR 0004.
export function getPhysicalStatusHint(order: DgcOrderDetail, now: number = Date.now()): string | null {
  const issuer = order.issuance_driver === 'premo' ? ' with Premo' : '';

  if (order.status === 'PAID') {
    const stuck = now - new Date(order.updated_at).getTime() > STUCK_PAID_THRESHOLD_MS;
    return stuck
      ? `Payment is confirmed but card issuance${issuer} has not completed for a while. This order may be stuck.`
      : `Payment is confirmed and the cards are being issued${issuer}.`;
  }
  if (order.status === 'FAILED') {
    return `Card issuance${issuer} failed. The order can be retried by confirming payment again.`;
  }
  return null;
}
